import React, { useState } from "react";
import { Box } from "@mui/material";
import { Typography } from "@mui/joy";
import { AddRounded, RemoveRounded, SwapHorizRounded } from "@mui-symbols-material/w300";
import { ActionsMenu } from "@dreebit-registry/vsm-microservice-components";
import { WidgetConfig } from "../DashboardWidgetConfig";
import { WidgetActionsMenu } from "../WidgetActionsMenu";
import QuickActionAssetModal from "../QuickActionAssetModal";
import { ManualEventType } from "../../codegen/graphql";
import { QuickActionEventType } from "../../utils/quickActionAssetResolution";
import { useTranslation } from "react-i18next";

export type QuickActionType = 'removeAsset' | 'installAsset' | 'exchangeAsset';

interface QuickActionDefinition {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  eventType: QuickActionEventType;
}

interface QuickActionWidgetProps {
  onDelete: () => void;
  onEdit: () => void;
  config?: WidgetConfig;
}

const QuickActionWidget: React.FC<QuickActionWidgetProps> = ({ onDelete, onEdit, config }) => {
  const { t } = useTranslation();
  const actionType = (config?.actionType as QuickActionType) || 'removeAsset';
  const [modalOpen, setModalOpen] = useState(false);
  const actionItems = WidgetActionsMenu(onDelete, onEdit);

  const iconSx = { fontSize: "clamp(28px, 20cqi, 40px)", color: "#666", flexShrink: 0 };

  const ACTION_DEFINITIONS: Record<QuickActionType, QuickActionDefinition> = {
    removeAsset: {
      title: t('Remove asset'),
      subtitle: t('Remove a device from a location'),
      icon: <RemoveRounded sx={iconSx} />,
      eventType: ManualEventType.ASSET_REMOVE,
    },
    installAsset: {
      title: t('Install asset'),
      subtitle: t('Add a new device to a location'),
      icon: <AddRounded sx={iconSx} />,
      eventType: ManualEventType.ASSET_INSTALL,
    },
    exchangeAsset: {
      title: t('Exchange asset'),
      subtitle: t('Replace a device at a location'),
      icon: <SwapHorizRounded sx={iconSx} />,
      eventType: ManualEventType.ASSET_EXCHANGE,
    },
  };

  const action = ACTION_DEFINITIONS[actionType];

  const textOverflowSx = {
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "#666",
    minWidth: 0,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", position: "relative", containerType: "inline-size" }}>
      <div className="no-drag" style={{ position: "absolute", top: 0, right: 0, zIndex: 1 }}>
        <ActionsMenu items={actionItems} />
      </div>
      <Box
        className="no-drag"
        onClick={() => setModalOpen(true)}
        sx={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          gap: "12px",
          padding: "13px",
          cursor: "pointer",
          minWidth: 0,
        }}
      >
        {action.icon}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            level="titleLarge"
            sx={{ ...textOverflowSx, fontSize: "clamp(0.75rem, 9cqi, 1.125rem)", lineHeight: 1.2 }}
          >
            {action.title}
          </Typography>
          <Typography
            level="captionBold"
            sx={{ ...textOverflowSx, fontSize: "clamp(0.625rem, 6cqi, 0.75rem)", lineHeight: 1.3 }}
          >
            {action.subtitle}
          </Typography>
        </Box>
      </Box>

      {modalOpen && (
        <QuickActionAssetModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title={action.title}
          eventType={action.eventType}
        />
      )}
    </div>
  );
};

export default QuickActionWidget;
